import { OfficeWrapper } from "./OfficeWrapper";
import { Logger, Log } from "./Logger";
import { GraphRequestWrapper } from "./GraphRequestWrapper";
import {
    MsGraphGetMessageResponse,
    MsGraphGetMessagesResponse,
    MsGraphMessageItem,
    MsGraphGetEmailAttachmentResponse,
    MsGraphItemTypeAttachmentExpandedDetailsResponse,
} from "../models/MsGraphMessageResponse";
import { EmailListItem, EmailListResponse } from "../models/EmailListResponse";
import { MsGraphGetFolderResponse } from "../models/MsGraphGetFolderResponse";
import { ClearMessageService } from "./ClearMessageService";
interface EmailMessage {
    id: string;
    subject: string;
    conversationId: string;
    parentFolderId: string;
    createdDateTime: string;
}
interface MailFolder {
    id: string;
    displayName: string;
}

const newformaFolderNames: Record<string, string> = {
    en: "Newforma - Items to File",
    de: "Newforma - Abzulegende Elemente",
    nl: "Newforma-items naar bestand",
    fr: "Newforma - Éléments à enregistrer",
};

function getLocalizedNewformaFolderName(culture: string): string {
    const languageCode = culture.split("-")[0];
    return newformaFolderNames[languageCode];
}

let newformaFolderId: any;
let newformaFolderCulture: string | undefined;

export { newformaFolderId };

let setupFolderPromise: Promise<void> | null = null;
let setupFolderCulture: string | undefined;

/** Resets the cached Newforma folder ID so setupFolders will re-run. */
export function resetNewformaFolderId(): void {
    newformaFolderId = undefined;
    newformaFolderCulture = undefined;
    setupFolderPromise = null;
    setupFolderCulture = undefined;
}

/**
 * Returns a promise that resolves once setupFolders has completed.
 * If setupFolders hasn't been called yet, resolves immediately (caller should guard on newformaFolderId).
 * Use this to wait for the folder ID without blocking the login transition.
 */
export async function waitForNewformaFolderId(): Promise<string | undefined> {
    if (newformaFolderId) {
        return newformaFolderId;
    }
    if (setupFolderPromise) {
        await setupFolderPromise;
    }
    return newformaFolderId;
}

export class MsGraphApiService {
    private readonly graphUrl = "https://graph.microsoft.com/v1.0";
    private readonly retryAfterHeaderKey = "Retry-After";
    private readonly defaultRetryTimeInSeconds = 1;
    private readonly maxRetryAttempts = 5;
    private readonly messageListPageSize = 50;
    private readonly filedMessageCategory = ["Filed by Newforma", "Abgelegt durch Newforma", "Archivé par Newforma"];
    private sentItemsFolderId: string | undefined;
    private inboxFolderId: string | undefined;
    private readonly folderNameById = new Map<string, string>();
    private readonly folderNameRequestById = new Map<string, Promise<string>>();

    // Concurrency cap: Graph allows ~4 concurrent requests per app per mailbox
    private readonly maxConcurrentRequests = 3;
    private activeRequests = 0;
    private readonly waiters: Array<() => void> = [];

    constructor(
        private officeWrapper: OfficeWrapper,
        private graphRequestWrapper: GraphRequestWrapper,
        private clearMessageService: ClearMessageService,
        private logger: Logger
    ) {}

    async getCurrentFolderId(): Promise<string> {
        const messageDetails = await this.getCurrentMessageResponseData();
        return messageDetails.parentFolderId;
    }

    async getFolderName(folderId: string): Promise<string> {
        const folderResponseData = await this.getFolderResponseData(folderId);
        return folderResponseData.displayName;
    }

    async getCachedFolderName(folderId: string): Promise<string> {
        const cachedFolderName = this.folderNameById.get(folderId);
        if (cachedFolderName) {
            return cachedFolderName;
        }

        const existingRequest = this.folderNameRequestById.get(folderId);
        if (existingRequest) {
            return existingRequest;
        }

        const request = this.getFolderResponseData(folderId)
            .then((folderResponseData) => {
                this.folderNameById.set(folderId, folderResponseData.displayName);
                return folderResponseData.displayName;
            })
            .finally(() => {
                this.folderNameRequestById.delete(folderId);
            });

        this.folderNameRequestById.set(folderId, request);
        return request;
    }

    async getCurrentMessageAsEmailListItem(): Promise<EmailListItem> {
        const messageDetails = await this.getCurrentMessageResponseData();
        return this.convertMsGraphMessageItemToEmailListItem(messageDetails);
    }

    async getFolderEmails(
        folderId: string,
        offsetToken: string | null,
        searchValue: string | null
    ): Promise<EmailListResponse> {
        const emailsGraphResponse = await this.getFolderEmailsResponseData(folderId, offsetToken, searchValue);
        return this.mapMessagesResponseToEmailListResponse(emailsGraphResponse);
    }

    async getCurrentClearMessageBody(isFroalaEditorSupported: boolean): Promise<string> {
        const currentEmail = await this.getCurrentMessageResponseData();
        return this.clearMessageService.getClearMessageBody(currentEmail.body.content, isFroalaEditorSupported);
    }

    private async getFolderEmailsResponseData(
        folderId: string,
        offsetToken: string | null,
        searchValue: string | null
    ): Promise<MsGraphGetMessagesResponse> {
        this.logger.info(`Retrieving folder emails from graph API for folder: ${folderId}`);
        const searchFilter = !!searchValue ? `&$search="${searchValue}"` : "";
        let url = `${this.graphUrl}/me/mailFolders/${this.convertIdForGraphApi(folderId)}/messages?$top=${
            this.messageListPageSize
        }${searchFilter}`;
        if (!!offsetToken) {
            url = offsetToken;
        }
        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    private mapMessagesResponseToEmailListResponse(emailsGraphResponse: MsGraphGetMessagesResponse): EmailListResponse {
        return {
            items: emailsGraphResponse.value.map((email) => {
                return this.convertMsGraphMessageItemToEmailListItem(email);
            }),
            paging: {
                offsetToken: emailsGraphResponse["@odata.nextLink"],
            },
        };
    }

    private isFiled(categories: string[]): boolean {
        if (categories && !categories.length) {
            return false;
        }
        return categories.some((r) => this.filedMessageCategory.includes(r));
    }

    async getChildrenFolders(parentFolderId: string): Promise<MailFolder[]> {
        Log.info("Retrieving Outlook Folders");
        if (!parentFolderId) {
            return [];
        }
        let url = `${this.graphUrl}/me/mailFolders/${parentFolderId}/childFolders?$top=50`;
        const headers = {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
        };

        const folders: MailFolder[] = [];
        while (url) {
            const response = await this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url, "json", headers));

            if (response && response.value) {
                folders.push(...response.value);
            }

            url = response["@odata.nextLink"] || null;
        }
        return folders;
    }

    async getWellKnownFolderId(wellKnownFolder: string): Promise<string> {
        const url = `${this.graphUrl}/me/mailFolders/${wellKnownFolder}`;
        const options = JSON.stringify({
            "Content-Type": "application/json",
        });
        const response = await this.makeRequestWithRetry(() =>
            this.graphRequestWrapper.get(url, "json", undefined, undefined, options)
        );
        if (!response) {
            return "";
        }

        return response?.id;
    }

    async getCustomFolderId(folderName: string): Promise<string> {
        let url = `${this.graphUrl}/me/mailFolders?$top=50`;
        const options = JSON.stringify({
            "Content-Type": "application/json",
        });
        const rootFolders: MailFolder[] = [];
        while (url) {
            const response = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.get(url, "json", undefined, undefined, options)
            );
            if (!response) {
                return "";
            }
            if (response && response.value) {
                rootFolders.push(...response.value);
            }

            url = response["@odata.nextLink"] || null;
        }

        const customFolder = rootFolders?.find((folder: any) => folder.displayName === folderName);
        this.logger.info(`Custom folder: ${customFolder}`);

        return customFolder?.id || "";
    }

    async createFolder(folderName: string): Promise<MsGraphGetMessageResponse> {
        Log.info(`Creating Folder [${folderName}]`);
        const url = `${this.graphUrl}/me/mailFolders`;
        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
        };
        const payload = JSON.stringify({ displayName: folderName });
        const options = JSON.stringify({ contentType: "application/json" });
        const response = await this.makeRequestWithRetry(() =>
            this.graphRequestWrapper.post(url, "json", headers, payload, options)
        );

        Log.info(response);
        return response;
    }

    async createSubfolder(parentFolderId: string, subfolderName: string): Promise<string | null> {
        Log.info(`createSubfolder - request: parent=${parentFolderId} name="${subfolderName}"`);
        if (!parentFolderId) {
            Log.error("createSubfolder - parentFolderId is empty, cannot create subfolder.");
            return null;
        }
        const folders = await this.getChildrenFolders(parentFolderId);
        const existing = folders?.find((folder: any) => folder.displayName === subfolderName);
        if (existing) {
            Log.info(
                `createSubfolder - found existing folder: parent=${parentFolderId} name="${subfolderName}" id=${existing.id}`
            );
            return existing.id;
        }

        const url = `${this.graphUrl}/me/mailFolders/${parentFolderId}/childFolders`;
        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
        };
        const payload = {
            displayName: subfolderName,
        };
        Log.info(`Creating Folder ${subfolderName}`);
        const payloadString = JSON.stringify(payload);

        try {
            const response = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.post(url, "json", headers, payloadString)
            );
            Log.info(
                `createSubfolder - created folder: parent=${parentFolderId} name="${subfolderName}" id=${response?.id} status=${response?.statusCode}`
            );
            return response?.id;
        } catch (error) {
            Log.error(`createSubfolder - error creating "${subfolderName}" under parent=${parentFolderId}:`, error);
            return null;
        }
    }

    async getSubfolder(parentFolderId: string): Promise<MailFolder[]> {
        const folders = await this.getChildrenFolders(parentFolderId);
        return folders;
    }

    /** Creates (or finds) a chain of nested subfolders, e.g. ["Subfolder1", "Subfolder2"], returning the leaf folder's id. */
    async createNestedSubfolders(parentFolderId: string, folderNames: string[]): Promise<string | null> {
        let currentParentId = parentFolderId;
        for (const folderName of folderNames) {
            const folderId = await this.createSubfolder(currentParentId, folderName);
            if (!folderId) {
                Log.error(`createNestedSubfolders - failed to create/find "${folderName}" under ${currentParentId}`);
                return null;
            }
            currentParentId = folderId;
        }
        return currentParentId;
    }

    /** Copies a message (by id) into the given mail folder, leaving the original in place. */
    async copyMessageToFolder(messageId: string, destinationFolderId: string): Promise<boolean> {
        Log.info(`copyMessageToFolder - request: messageId=${messageId} destinationFolderId=${destinationFolderId}`);
        if (!messageId || !destinationFolderId) {
            Log.error("copyMessageToFolder - messageId or destinationFolderId is empty.");
            return false;
        }
        const url = `${this.graphUrl}/me/messages/${this.convertIdForGraphApi(messageId)}/copy`;
        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
        };
        const payloadString = JSON.stringify({ destinationId: destinationFolderId });

        try {
            const response = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.post(url, "json", headers, payloadString)
            );
            Log.info(
                `copyMessageToFolder - success: messageId=${messageId} destinationFolderId=${destinationFolderId} newMessageId=${response?.id} status=${response?.statusCode}`
            );
            return true;
        } catch (error) {
            Log.error(
                `copyMessageToFolder - error copying messageId=${messageId} to destinationFolderId=${destinationFolderId}:`,
                error
            );
            return false;
        }
    }

    async getCurrentMessageResponseData(): Promise<MsGraphGetMessageResponse> {
        this.logger.info("Retrieving current message data from graph API");
        const itemId = this.officeWrapper.getCurrentMessageId();
        const url = `${this.graphUrl}/me/messages/${this.convertIdForGraphApi(itemId)}`;
        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    async getAttachmentDetails(): Promise<any> {
        const itemId = this.officeWrapper.getCurrentMessageId();
        const url = `${this.graphUrl}/me/messages/${this.convertIdForGraphApi(itemId)}/attachments`;
        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    private async getFolderResponseData(folderId: string): Promise<MsGraphGetFolderResponse> {
        this.logger.info("Retrieving folder emails from graph API");
        const url = `${this.graphUrl}/me/mailFolders/${this.convertIdForGraphApi(folderId)}`;
        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    private convertIdForGraphApi(id: string) {
        return id.replace(/\//g, "-").replace(/\+/g, "_");
    }

    private convertMsGraphMessageItemToEmailListItem(email: MsGraphMessageItem): EmailListItem {
        return {
            id: email.id,
            sentDateTime: email.sentDateTime,
            hasAttachments: email.hasAttachments,
            categories: email.categories || [],
            isFiled: this.isFiled(email.categories || []),

            subject: email.subject,
            bodyPreview: email.bodyPreview,
            sender: {
                ...(email.sender && { address: email.sender.emailAddress.address }),
                ...(email.sender && { name: email.sender.emailAddress.name }),
            },
            from: {
                ...(email.from && { address: email.from.emailAddress.address }),
                ...(email.from && { name: email.from.emailAddress.name }),
            },
        };
    }

    async getAttachment(messageId: string, attachmentId: string): Promise<MsGraphGetEmailAttachmentResponse> {
        this.logger.info("Call to graph API to get attachment");

        const graphApiMessageId = this.convertIdForGraphApi(messageId);
        const graphApiAttachmentId = this.convertIdForGraphApi(attachmentId);

        const url = `${this.graphUrl}/me/messages/${graphApiMessageId}/attachments/${graphApiAttachmentId}`;

        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    async getAttachmentRawValue(messageId: string, attachmentId: string): Promise<string> {
        this.logger.info("Call to graph API to get attachment raw value");

        const graphApiMessageId = this.convertIdForGraphApi(messageId);
        const graphApiAttachmentId = this.convertIdForGraphApi(attachmentId);

        const url = `${this.graphUrl}/me/messages/${graphApiMessageId}/attachments/${graphApiAttachmentId}/$value`;

        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    async getAttachmentItemTypeExpandedDetails(
        messageId: string,
        attachmentId: string
    ): Promise<MsGraphItemTypeAttachmentExpandedDetailsResponse> {
        this.logger.info("Retrieving email attachment expanded details from Outlook REST API");

        const graphApiMessageId = this.convertIdForGraphApi(messageId);
        const graphApiAttachmentId = this.convertIdForGraphApi(attachmentId);
        const url = `${this.graphUrl}/me/messages/${graphApiMessageId}/attachments/${graphApiAttachmentId}?$expand=Microsoft.graph.ItemAttachment/Item`;

        return this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url));
    }

    private async makeRequestWithRetry(request: () => Promise<any>, attemptCount: number = 1): Promise<any> {
        // Wait for a free slot so we never have more than maxConcurrentRequests calls in flight
        if (this.activeRequests >= this.maxConcurrentRequests) {
            await new Promise<void>((resolve) => this.waiters.push(resolve)); // slot is handed over by release()
        } else {
            this.activeRequests++;
        }
        let released = false;
        const release = () => {
            if (released) {
                return;
            }
            released = true;
            const next = this.waiters.shift();
            if (next) {
                next();
            } else {
                this.activeRequests--;
            }
        };

        try {
            return await request();
        } catch (error) {
            if (attemptCount === this.maxRetryAttempts) {
                this.logger.error(
                    `MsGraphApiService. Max retry attempts (${this.maxRetryAttempts}) reached for request.`,
                    error
                );
                throw error;
            }

            if ((error as any).status && (error as any).status === 429) {
                const retryAfter =
                    ((error as any).getResponseHeader(this.retryAfterHeaderKey) || this.defaultRetryTimeInSeconds) *
                    1000;
                this.logger.error(
                    `MsGraphApiService. The request was throttled by the office api on attempt (${attemptCount}). retrying after ${retryAfter}ms... `
                );
                release(); // don't hold a slot while waiting
                await new Promise((resolve) => {
                    setTimeout(resolve, retryAfter);
                });
                return this.makeRequestWithRetry(request, attemptCount + 1);
            }

            throw error;
        } finally {
            release();
        }
    }

    async getUserProfile(): Promise<string | undefined> {
        let userPrincipalName;
        try {
            const url = `${this.graphUrl}/me`;
            const userData = await this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url, "json"));

            Log.info(`UPN ${userData?.userPrincipalName}`);
            Log.info("Success getting UPN");

            userPrincipalName = userData?.userPrincipalName;
        } catch (error) {
            Log.error("Error getting UPN:", error);
        }
        return userPrincipalName;
    }

    async getUserIdByUPN(userPrincipalName: string): Promise<string | null> {
        const url = `${this.graphUrl}/users/${userPrincipalName}`;
        try {
            const response = await this.makeRequestWithRetry(() => this.graphRequestWrapper.get(url, "json"));
            return response?.id ?? "";
        } catch (error) {
            this.logger.error(`Error getting user ID by UPN (${userPrincipalName}):`, error);
            return null;
        }
    }

    async setFolderExtendedProperty(folderId: string, propertyName: string, propertyValue: string): Promise<void> {
        const url = `${this.graphUrl}/me/mailFolders/${folderId}`;

        const headers = { "Content-Type": "application/json" };
        // Microsoft recommends that the guid below be used: DO NOT CHANGE
        const requestBody = {
            singleValueExtendedProperties: [
                {
                    id: `String {00020329-0000-0000-C000-000000000046} Name ${propertyName}`,
                    value: propertyValue,
                },
            ],
        };

        try {
            await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.patch(url, "json", headers, JSON.stringify(requestBody))
            );
            Log.info(`Successfully set property ${propertyName} on folder ${folderId}`);
        } catch (error) {
            Log.error(`Error setting property: ${error}`);
            throw error;
        }
    }

    async getFolderExtendedProperty(folderId: string, propertyName: string): Promise<string | null> {
        // Microsoft recommends that the guid below be used: DO NOT CHANGE
        const url = `${this.graphUrl}/me/mailFolders/${folderId}`;
        const propertyId = `String {00020329-0000-0000-C000-000000000046} Name ${propertyName}`;
        const graphApiUrl = `${url}?$expand=singleValueExtendedProperties($filter=id eq '${encodeURIComponent(
            propertyId
        )}')`;

        try {
            const headers = { "Content-Type": "application/json" };
            const response = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.get(graphApiUrl, "json", headers)
            );

            if (response && (response?.singleValueExtendedProperties?.length ?? 0) > 0) {
                return response?.singleValueExtendedProperties[0].value;
            } else {
                Log.info(`Property ${propertyName} not found on folder ${folderId}`);
                return null;
            }
        } catch (error) {
            Log.error(`Error reading property ${propertyName} : ${error}`);
            throw error;
        }
    }
    // the following comment line is used to mark blocks of text for which a unittest is not appropriate due to complexity
    /* istanbul ignore start */

    async setupFolders(culture?: string): Promise<void> {
        if (!culture) {
            Log.info("Skipping Newforma folder setup until culture is available");
            return;
        }

        // 1. Return immediately if already populated for this culture
        if (newformaFolderId && newformaFolderCulture === culture) {
            return;
        }

        // 2. If existing folder or in-flight setup belongs to a different culture, reset cache
        if (
            (newformaFolderCulture && newformaFolderCulture !== culture) ||
            (setupFolderCulture && setupFolderCulture !== culture)
        ) {
            resetNewformaFolderId();
            setupFolderPromise = null;
            setupFolderCulture = undefined;
        }

        // 3. Deduplicate in-flight requests for the same culture
        if (!setupFolderPromise) {
            setupFolderCulture = culture;
            setupFolderPromise = (async () => {
                try {
                    await this._doSetupFolders(culture);
                } finally {
                    // Clear only if this promise is still the active request and setup failed
                    if (setupFolderCulture === culture && !newformaFolderId) {
                        setupFolderPromise = null;
                        setupFolderCulture = undefined;
                    }
                }
            })();
        }

        await setupFolderPromise;
    }

    private async _doSetupFolders(culture?: string | undefined): Promise<void> {
        try {
            const newformaFolder = getLocalizedNewformaFolderName(culture || "en-US");

            newformaFolderId = await this.getCustomFolderId(newformaFolder);
            Log.info(`Newforma Folder Id [${newformaFolderId}]`);

            if (!newformaFolderId) {
                Log.info(`Newforma Folder id doesn't exist, attempt to Create it. `);

                const response = await this.createFolder(newformaFolder);

                newformaFolderId = await this.getCustomFolderId(newformaFolder);
                Log.info(`Newforma Folder Id [${newformaFolderId}]`);
            } else {
                newformaFolderId = encodeURIComponent(newformaFolderId);
            }
            if (newformaFolderId) {
                newformaFolderCulture = culture;
            }
        } catch {
            Log.error("Unexpected error checking for Newforma Folder");
        }
    }
    /* istanbul ignore end */

    /**
     * Gets all email messages in a conversation, optionally filtering for those received on or before the starting email's date.
     * @param emailId The ID of the email message to use as a starting point.
     * @param includeSameOrOlderDate If true, only include emails with a receivedDateTime <= the starting email's receivedDateTime.
     * @returns A promise that resolves to a list of filtered email messages.
     */
    async getConversationEmails(
        emailId: string,
        includeSameOrOlderDate: boolean = false
    ): Promise<EmailMessage[] | undefined> {
        let conversationId: string | undefined;
        let parentFolderId: string | undefined;

        let createdDateTime: string | undefined;
        let conversationMessages: EmailMessage[] | undefined;

        try {
            // 1. Get Conversation ID AND Received Date of the starting email
            Log.info(`Fetching conversation ID and date for email: ${emailId}`);

            // ** Important, use 'createdDateTime', and also parentFolderId
            const conversationIdUrl = `${this.graphUrl}/me/messages/${emailId}?$select=conversationId,parentFolderId,createdDateTime`;

            const emailData = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.get(conversationIdUrl, "json")
            );

            conversationId = emailData?.conversationId;
            parentFolderId = emailData?.parentFolderId;
            createdDateTime = emailData?.createdDateTime;

            if (!conversationId) {
                Log.warning(`No conversation ID found for email: ${emailId}`);
                return [];
            }

            // 2. Build the URL to retrieve all messages in that conversation
            const filters: any = [];

            if (conversationId) {
                // CONDITION 1: Conversation ID
                filters.push(`conversationId eq '${encodeURIComponent(conversationId)}'`);
                // CONDITION 2:Only allow conversation email from Parent folder of selected email, and 'Sent Items' folder

                // Reuse folderId's within the lifetime of this object, as getting the folderId, one-time can take approx 0.5 secs
                // hence reusing the acquired Id can save a lot of time if multiple emails are being filed.
                // A well known folder id , will be very stable, BUT can change if exchange server is migrated
                if (!this.inboxFolderId) {
                    this.inboxFolderId = await this.getWellKnownFolderId("inbox");
                }
                if (!this.sentItemsFolderId) {
                    this.sentItemsFolderId = await this.getWellKnownFolderId("sentItems");
                }

                Log.info(`parent    Id:${parentFolderId}`);
                Log.info(`inbox     Id:${this.inboxFolderId}`);
                Log.info(`sentItems Id:${this.sentItemsFolderId}`);

                const includeFolders: any = [];
                if (parentFolderId) {
                    includeFolders.push(`parentFolderId eq '${encodeURIComponent(parentFolderId)}'`);
                }
                if (this.inboxFolderId) {
                    includeFolders.push(`parentFolderId eq '${encodeURIComponent(this.inboxFolderId)}'`);
                }
                if (this.sentItemsFolderId) {
                    includeFolders.push(`parentFolderId eq '${encodeURIComponent(this.sentItemsFolderId)}'`);
                }
                if (includeFolders?.length > 0) {
                    filters.push(`(${includeFolders.join(" or ")})`);
                }
            }

            const conversationFilter = filters.join(" and ");
            const conversationUrl = `${this.graphUrl}/me/messages?$filter=${conversationFilter}`;

            const conversationData = await this.makeRequestWithRetry(() =>
                this.graphRequestWrapper.get(conversationUrl, "json")
            );
            // extract the messages from the response
            conversationMessages = conversationData?.value;

            if (!!createdDateTime) {
                const created = +new Date(createdDateTime ?? "2100");
                Log.info(`Found conversation ID: ${conversationId}, Created Date: ${createdDateTime}`);
                conversationMessages = conversationMessages?.filter((item) => {
                    const itemCreated = +new Date(item.createdDateTime);
                    return itemCreated <= created;
                });
            }
            conversationMessages?.map((item, index) => {
                Log.info(`${index}  Subject: ${item.subject}`);
            });
            Log.info(`Successfully retrieved ${conversationMessages?.length || 0} filtered messages.`);
        } catch (error) {
            Log.error(`Error retrieving conversation emails for ID ${emailId}:`, error);
        }

        return conversationMessages || [];
    }

    async getAllConversationEmails(emailIds: string[]): Promise<EmailMessage[] | undefined> {
        if (!emailIds || emailIds.length === 0) {
            return undefined;
        }

        const messages: EmailMessage[] = [];
        // cycle through all emailIds.. get the conversations and put in a master list eliminating dupes
        for (const emailId of emailIds) {
            // convert 1 email into its conversation
            const targets = await this.getConversationEmails(emailId);
            // now for each email from the conversation , check if it is already in the list
            // this could happen if a user accidentally selects two emails in the same conversation
            targets?.map((itemTarget) => {
                const foundItem = messages.find((item) => item.id === itemTarget.id);
                if (!foundItem) {
                    // its new!
                    messages.push(itemTarget);
                }
            });
        }
        return messages || [];
    }
    async expandEmailsForConversationFiling(selectedEmailIds: string[]): Promise<void> {
        this.logger.info(`Conversation Filing ON`);
        this.logger.info(`Original to be filed ${selectedEmailIds.length} email(s)`);

        // 1. Call the async service to get expanded emails
        // Note: The service should handle deduplication, but the subsequent code
        // will still ensure only the 'id' strings are kept.
        const newEmailObjects = await this.getAllConversationEmails(selectedEmailIds);

        // 2. Clear the original array of IDs prior to repopulating (mutating the passed array)
        selectedEmailIds.length = 0;

        // 3. Populate the array with the new, expanded list of *ID strings*
        if (newEmailObjects) {
            // Use for...of to iterate over the email objects
            for (const email of newEmailObjects) {
                // Push the unique ID string onto the array
                selectedEmailIds.push(email.id);
            }
        }

        this.logger.info(`Conversation expanded to ${selectedEmailIds.length} email(s)`);
    }
}
